'use client';

import { useState } from 'react';
import {
  Magnet,
  ChevronUp,
  ChevronDown,
  StickyNote,
  Square,
  Type,
  Spline,
} from 'lucide-react';
import { Tip } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useT } from '@/components/i18n-provider';
import {
  usePendingAnnotation,
  useWorkspaceActions,
} from '@/components/workspace/store';

/**
 * Vertical tools column sitting above the workspace `+` FAB (legacy chrome
 * only: it renders when railV2 is off, which the sticky `?rail=0` override
 * still allows in production).
 *
 * The annotation strip, revealed by tapping the expand chevron. Tools:
 * sticky / shape / text / connector + magnet. Picking a tool sets
 * `pendingAnnotation` on the store — the canvas pane click handler reads
 * that slice and drops the matching widget at the click point (Phase 3
 * wiring; see workspace-canvas.tsx). While expanded the chevron flips to a
 * collapse arrow at the top of the strip.
 *
 * The column used to end in a zoom cluster (plus / minus / fit view) that
 * called React Flow directly, so `?rail=0` put working zoom buttons on a
 * canvas whose zoom is disabled. Removed 2026-09-26; the name stays because
 * the wrapper class and call site use it.
 */
export function ZoomControls({
  className,
  snap,
  onToggleSnap,
}: {
  className?: string;
  snap: boolean;
  onToggleSnap: () => void;
}) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const pending = usePendingAnnotation();
  const { setPendingAnnotation } = useWorkspaceActions();

  const buttonCls =
    'h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-accent text-muted-foreground/70 hover:text-foreground';
  const activeCls = 'bg-primary/80 text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground';

  // Helper: select an annotation tool. Re-selecting the same tool
  // toggles it off (the user cancelled their intent). Connector mode
  // stays armed across drags so the user can draw multiple lines.
  const pickTool = (
    pick:
      | { kind: 'sticky' | 'text' }
      | { kind: 'shape'; shape?: 'rectangle' | 'ellipse' | 'diamond' | 'triangle' }
      | { kind: 'connector' },
  ) => {
    if (
      pending &&
      pending.kind === pick.kind &&
      ('shape' in pending ? pending.shape : undefined) ===
        ('shape' in pick ? pick.shape : undefined)
    ) {
      setPendingAnnotation(null);
      return;
    }
    if (pick.kind === 'shape') {
      setPendingAnnotation({ kind: 'shape', shape: pick.shape ?? 'rectangle' });
    } else if (pick.kind === 'sticky' || pick.kind === 'text') {
      setPendingAnnotation({ kind: pick.kind });
    } else {
      setPendingAnnotation({ kind: 'connector' });
    }
  };

  const stickyActive = pending?.kind === 'sticky';
  const shapeActive = pending?.kind === 'shape';
  const textActive = pending?.kind === 'text';
  const connectorActive = pending?.kind === 'connector';

  return (
    <div
      className={cn(
        // Same w-8 column the original toolbar used — the parent's
        // `left-3` keeps the column centred on the `+` FAB column.
        // We allow the column to grow downward as the strip expands by
        // using `flex-col` + `gap-2`; the max-height animation is
        // applied to the annotation strip wrapper below so the zoom
        // cluster stays anchored at the bottom.
        'liquid-glass pointer-events-auto inline-flex w-8 flex-col items-center gap-2 rounded-xl px-1 py-1 select-none',
        className,
      )}
    >
      {/*
        Annotation strip — wraps in a clipped container with a
        max-height + opacity transition so it grows in / out smoothly.
        Same cubic-bezier curve every other glass surface in the
        workspace uses. While expanded, the strip's first button is
        the collapse chevron (replaces the expand chevron from below).
      */}
      <div
        className={cn(
          'flex flex-col items-center gap-2 overflow-hidden',
          'transition-[max-height,opacity] ease-[cubic-bezier(0.22,1,0.36,1)] duration-500',
          expanded ? 'max-h-72 opacity-100' : 'max-h-0 opacity-0 pointer-events-none',
        )}
        aria-hidden={!expanded}
      >
        <Tip label={t('tools.collapse')}>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            aria-label={t('workspace.zoom.collapseAnnotationTools')}
            className={buttonCls}
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </Tip>
        <Tip label={t('workspace.zoom.stickyNote')}>
          <button
            type="button"
            onClick={() => pickTool({ kind: 'sticky' })}
            aria-pressed={stickyActive}
            aria-label={t('workspace.zoom.stickyNote')}
            className={cn(buttonCls, stickyActive && activeCls)}
          >
            <StickyNote className="h-3 w-3" />
          </button>
        </Tip>
        <Tip label={t('workspace.zoom.shape')}>
          <button
            type="button"
            onClick={() => pickTool({ kind: 'shape' })}
            aria-pressed={shapeActive}
            aria-label={t('workspace.zoom.shape')}
            className={cn(buttonCls, shapeActive && activeCls)}
          >
            <Square className="h-3 w-3" />
          </button>
        </Tip>
        <Tip label={t('workspace.zoom.text')}>
          <button
            type="button"
            onClick={() => pickTool({ kind: 'text' })}
            aria-pressed={textActive}
            aria-label={t('workspace.zoom.text')}
            className={cn(buttonCls, textActive && activeCls)}
          >
            <Type className="h-3 w-3" />
          </button>
        </Tip>
        <Tip label={t('workspace.zoom.connector')}>
          <button
            type="button"
            onClick={() => pickTool({ kind: 'connector' })}
            aria-pressed={connectorActive}
            aria-label={t('workspace.zoom.connector')}
            className={cn(buttonCls, connectorActive && activeCls)}
          >
            <Spline className="h-3 w-3" />
          </button>
        </Tip>
        <Tip label={snap ? t('workspace.zoom.snapOn') : t('workspace.zoom.snapOff')}>
          <button
            type="button"
            onClick={onToggleSnap}
            aria-pressed={snap}
            aria-label={t('workspace.zoom.toggleSnap')}
            className={cn(buttonCls, snap && activeCls)}
          >
            <Magnet className="h-3 w-3" />
          </button>
        </Tip>
      </div>

      {!expanded && (
        <Tip label={t('workspace.zoom.annotationTools')}>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            aria-label={t('workspace.zoom.expandAnnotationTools')}
            className={buttonCls}
          >
            <ChevronUp className="h-3 w-3" />
          </button>
        </Tip>
      )}
    </div>
  );
}
